import { useState } from "react";
import { PostingSummary, FilterOptions } from "@/lib/types";
import { SUBJECT_OPTIONS, SCHEME_OPTIONS, LEVEL_OPTIONS } from "@/lib/utils";

interface Props {
  posting: PostingSummary;
  currentFilters: Partial<FilterOptions>;
  onApply: (filters: Partial<FilterOptions>) => void;
  onReset: () => void;
  onClose: () => void;
}

export default function FilterSheet({
  posting,
  currentFilters,
  onApply,
  onReset,
  onClose,
}: Props) {
  const [experience, setExperience] = useState(
    currentFilters.minExperience ?? 0
  );
  const [selectedSubjects, setSelectedSubjects] = useState<Set<string>>(
    new Set(currentFilters.subjects ?? [])
  );
  const [selectedLevels, setSelectedLevels] = useState<Set<string>>(
    new Set(currentFilters.levels ?? [])
  );
  const [selectedSchemes, setSelectedSchemes] = useState<Set<string>>(
    new Set(currentFilters.scheme ?? [])
  );

  const handleSubjectToggle = (subject: string) => {
    setSelectedSubjects((prev) => {
      const next = new Set(prev);
      if (next.has(subject)) {
        next.delete(subject);
      } else {
        next.add(subject);
      }
      return next;
    });
  };

  const handleLevelToggle = (level: string) => {
    setSelectedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) {
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
  };

  const handleApply = () => {
    onApply({
      minExperience: experience > 0 ? experience : null,
      subjects:
        selectedSubjects.size > 0 ? Array.from(selectedSubjects) : undefined,
      levels: selectedLevels.size > 0 ? Array.from(selectedLevels) : undefined,
      scheme:
        selectedSchemes.size > 0 ? Array.from(selectedSchemes) : undefined,
    });
  };

  const handleReset = () => {
    setExperience(0);
    setSelectedSubjects(new Set());
    setSelectedLevels(new Set());
    setSelectedSchemes(new Set());
    onReset();
  };

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-white px-4 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold">Filter Applicants</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <CloseIcon />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Experience Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-medium text-gray-900">Experience</label>
              <span className="text-gray-700 font-medium">
                {experience === 0 ? "Any" : `${experience}+ years`}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="20"
              value={experience}
              onChange={(e) => setExperience(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
            />
          </div>

          {/* Subjects */}
          <div>
            <label className="font-medium text-gray-900 block mb-2">
              Subjects
            </label>
            <div className="flex flex-wrap gap-2">
              {SUBJECT_OPTIONS.map((subject) => (
                <button
                  key={subject}
                  onClick={() => handleSubjectToggle(subject)}
                  className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    selectedSubjects.has(subject)
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  {subject}
                </button>
              ))}
            </div>
          </div>

          {/* Levels */}
          <div>
            <label className="font-medium text-gray-900 block mb-2">
              Levels
            </label>
            <div className="grid grid-cols-2 gap-2">
              {LEVEL_OPTIONS.map((level) => (
                <label
                  key={level}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedLevels.has(level)}
                    onChange={() => handleLevelToggle(level)}
                    className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-gray-700">{level}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Scheme */}
          <div>
            <label className="font-medium text-gray-900 block mb-2">
              Scheme
            </label>
            <div className="flex flex-wrap gap-2">
              {SCHEME_OPTIONS.map((scheme) => (
                <button
                  key={scheme}
                  onClick={() =>
                    setSelectedSchemes((prev) => {
                      const next = new Set(prev);
                      if (next.has(scheme)) {
                        next.delete(scheme);
                      } else {
                        next.add(scheme);
                      }
                      return next;
                    })
                  }
                  className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    selectedSchemes.has(scheme)
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  {scheme}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="sticky bottom-0 bg-white px-4 py-4 border-t flex gap-3">
          <button
            onClick={handleReset}
            className="flex-1 py-3 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-3 px-4 rounded-lg bg-primary-700 text-white font-medium hover:bg-primary-600"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
